import { FC, useEffect, useRef, useState } from "react";
import { GroupOutlined } from "@ant-design/icons";
import { Button, DatePicker, Flex, Input, notification, TableProps, Tooltip, Typography } from "antd";
import STableLayout, {
  DEFAULT_PAGE_SIZE,
  TableLayoutRef,
} from "@/styles/STableLayout";
import apis, { ListParams } from "@/apis/requirement";
import useDataList from "@/hooks/useDataList";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import { useTranslation } from "react-i18next";
import SText from "@/styles/SText";
import { Requirement, RequirementConfirmTypeEnum, RequirementStatusEnum } from "@/types/requirement";
import useRequirementParams from "../../hooks/useRequirementParams";
import { CommonListParams } from "@/types/common";
import RequirementDetail from "../Detail";
import RequirementReview from "../../components/RequirementReview";
import useRequirementLabel from "../../hooks/useRequirementLabel";
import { dateFormat } from "@/utils/format";
import dayjs, { Dayjs } from "dayjs";
import { PageId } from "@/types/page";
import { PurchaseModal } from "../Detail/components/SelectedQuotation";
import FinanceDetail from "../Detail/FinanceDetail";
import RequirementPurchaseOrders from "../../components/RequirementPurchaseOrders";
import AddMaterialOverIssue from "@/pages/Reimburse/components/AddMaterialOverIssue";
import { numFormmater } from "../../util";
import ExportExcelButton from "@/components/ExportExcelButton";
import ExportProgressModal from "@/components/ExportProgressModal";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";
import WMSAvatar from "@/styles/WMSAvatar";
import { getUserAvatarTip, getUserName } from "@/utils/user";

const { RangePicker } = DatePicker;

interface RequirementFilters {
  keyword: string;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
}

const INITIAL_REQUIREMENT_FILTERS: RequirementFilters = {
  keyword: "",
  startTime: null,
  endTime: null,
};

const RequirementList: FC = () => {
  const { t } = useTranslation();

  const { params, pageId } = useRequirementParams(true);
  const { getLabelShow } = useRequirementLabel();

  const tableRef = useRef<TableLayoutRef | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [list, total, listLoading, { fetchList, rmItem, updateItem }] =
    useDataList<Requirement, [CommonListParams<ListParams>]>({
      fetchApi: apis.list,
    });

  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<RequirementFilters>(INITIAL_REQUIREMENT_FILTERS);
  const { keyword, startTime, endTime } = draftFilters;
  const [detail, setDetail] = useState<Requirement | null>(null);
  const [purDetail, setPurDetail] = useState<Requirement | null>(null);
  const [requirenment, setRequirenment] = useState<Requirement | null>(null);
  const [overIssue, setOverIssue] = useState<Requirement | null>(null);
  const [allData, setAllData] = useState<Requirement[]>([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  // ✅ pageId 变化时取消请求
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [pageId]);

  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_REQUIREMENT_FILTERS);
    if (params.params.length > 0) {
      fetch(0, nextFilters);
    }
    tableRef?.current && tableRef.current.resetTablePage()
    // 切换页面时重置并重新请求，不能跟随每次 render 生成的 fetch 重复执行。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, replaceFilters]);

  const handleExportAll = async (): Promise<Requirement[]> => {
    const batchSize = 100;
    const allDataList: Requirement[] = [];

    setExportLoading(true);
    setExportProgress(0);

    try {
      if (!params) return [];

      const maps: {
        [key in string]: RequirementStatusEnum;
      } = {
        [PageId.MC_OFFICIAL_CLOSED]: RequirementStatusEnum.CLOSE,
        [PageId.PROD_OFFICIAL_CLOSED]: RequirementStatusEnum.CLOSE,
        [PageId.PUR_OFFICIAL_CLOSED]: RequirementStatusEnum.CLOSE,
        [PageId.MC_OFFICIAL_DONE]: RequirementStatusEnum.OPEN_DONE,
        [PageId.PROD_OFFICIAL_DONE]: RequirementStatusEnum.OPEN_DONE,
        [PageId.PUR_OFFICIAL_DONE]: RequirementStatusEnum.OPEN_DONE,
        [PageId.MC_OFFICIAL_PENDING]: RequirementStatusEnum.OPEN_PENDING,
        [PageId.PROD_OFFICIAL_PENDING]: RequirementStatusEnum.OPEN_PENDING,
        [PageId.PUR_OFFICIAL_PENDING]: RequirementStatusEnum.OPEN_PENDING,
        [PageId.MC_REQ_MANAGE]: RequirementStatusEnum.OPEN_PENDING,
        [PageId.MC_REQ_M]: RequirementStatusEnum.OPEN_PENDING,
      };

      let status: RequirementStatusEnum =
        (pageId && maps[pageId]) ?? RequirementStatusEnum.OPEN_PENDING;

      const firstRes = await apis.list({
        pagination: { offset: 0, take: batchSize },
        body: {
          keyword: appliedFilters.keyword,
          states: pageId && [PageId.MC_REQ_MANAGE, PageId.MC_REQ_M, PageId.PUR_OM].includes(pageId) ? [] : params.params,
          status,
          mrd: pageId === PageId.PUR_OM ? dayjs().startOf("day").valueOf() : undefined
        },
        startTime: pageId === PageId.PUR_OM
          ? undefined
          : appliedFilters.startTime?.startOf("day").valueOf(),
        endTime: pageId === PageId.PUR_OM
          ? undefined
          : appliedFilters.endTime?.endOf("day").valueOf(),
      });

      if (firstRes?.body) {
        allDataList.push(...firstRes.body);
      }

      const totalSize = firstRes?.pagination?.size ?? 0;
      const batches = Math.ceil(totalSize / batchSize);

      for (let i = 1; i < batches; i++) {
        const offset = i * batchSize;
        const res = await apis.list({
          pagination: { offset, take: batchSize },
          body: {
            keyword: appliedFilters.keyword,
            states: pageId && [PageId.MC_REQ_MANAGE, PageId.MC_REQ_M, PageId.PUR_OM].includes(pageId) ? [] : params.params,
            status,
            mrd: pageId === PageId.PUR_OM ? dayjs().startOf("day").valueOf() : undefined
          },
          startTime: pageId === PageId.PUR_OM
            ? undefined
            : appliedFilters.startTime?.startOf("day").valueOf(),
          endTime: pageId === PageId.PUR_OM
            ? undefined
            : appliedFilters.endTime?.endOf("day").valueOf(),
        });
        if (res?.body) {
          allDataList.push(...res.body);
        }
        setExportProgress(Math.round(((i + 1) / batches) * 100));
      }

      setAllData(allDataList);
      return allDataList;
    } catch (error) {
      notification.error({
        placement: 'top',
        message: t('common.exportErr'),
        description: `${t('requirement.total')}: ${allDataList.length}`
      });
      throw error;
    } finally {
      setExportLoading(false);
      setExportProgress(0);
    }
  };

  const requirementColumns = [
    { key: 'innerNumber', label: t('sale.innerNumber') },
    { key: 'number', label: t('requirement.number') },
    { key: 'orderDate', label: t('requirement.orderDate') },
    { key: 'code', label: t('product.code') },
    { key: 'name', label: t('product.name') },
    { key: 'brand1', label: t('product.brand1') },
    { key: 'uom', label: t('product.uom') },
    { key: 'requireCount', label: t('requirement.requireCount') },
    { key: 'mrd', label: t('requirement.mrd') },
    { key: 'eps', label: t('requirement.eps') },
    { key: 'estimatedCost', label: t('requirement.estimatedCost') },
    { key: 'desc', label: t('common.desc') },
    { key: 'salePrice', label: t('requirement.salePrice') },
    { key: 'mateCost', label: t('requirement.mateCost') },
    { key: 'netProfit', label: t('requirement.netProfit') },
    { key: 'purchaseProgress', label: t('requirement.purchaseProgress') },
    { key: 'quotationProgress', label: t('requirement.quotationProgress') },
    { key: 'materialProgress', label: t('requirement.materialProgress') },
  ];

  const requirementDataMapper = (item: Requirement, t: any) => ({
    [t('sale.innerNumber')]: item?.saleOrder?.innerNumber ?? '',
    [t('requirement.number')]: item?.number ?? '',
    [t('requirement.orderDate')]: item?.createdAt ? dateFormat(dayjs(item?.createdAt)) : '',
    [t('product.code')]: item?.product?.code ?? '',
    [t('product.name')]: item?.product?.name ?? '',
    [t('product.brand1')]: item?.product?.model ?? '',
    [t('product.uom')]: item?.product?.uom ?? '',
    [t('requirement.requireCount')]: item?.requireCount ?? '',
    [t('requirement.mrd')]: item?.mrd ? dateFormat(dayjs(item?.mrd)) : '',
    [t('requirement.eps')]: item?.eps ? dateFormat(dayjs(item?.eps)) : '',
    [t('requirement.estimatedCost')]: item?.estimatedCost ?? '',
    [t('common.desc')]: item?.desc ?? '',
    [t('requirement.salePrice')]: item?.saleOrder?.totalAmount ? numFormmater(Number(item.saleOrder.totalAmount)) : '',
    [t('requirement.mateCost')]: item?.saleOrder?.bomCost && !Number.isNaN(Number(item.saleOrder.bomCost)) ? numFormmater(Number(item.saleOrder.bomCost)) : '', // 处理空值和非数字值
    [t('requirement.netProfit')]: item?.saleOrder?.netProfit && !Number.isNaN(Number(item.saleOrder.netProfit)) ? numFormmater(Number(item.saleOrder.netProfit), 5) : '', // 处理空值和非数字值
    [t('requirement.purchaseProgress')]: `${item?.puredItemCount || 0}/${item?.itemCount || 0}`,
    [t('requirement.quotationProgress')]: `${item?.quoteItemCount || 0}/${item?.itemCount || 0}`,
    [t('requirement.materialProgress')]: `${item?.readyCount || 0}/${item?.itemCount || 0}`,
  });

  const handleSearch = () => {
    const nextFilters = commitFilters();
    fetch(0, nextFilters);
    tableRef?.current?.resetTablePage();
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_REQUIREMENT_FILTERS);
    fetch(0, nextFilters);
    tableRef?.current?.resetTablePage();
  };

  const headerChildren = (
    <Flex justify="space-between" gap={10} align="center" style={{ width: "100%" }}>
      <Flex gap={10}>
        <ExportExcelButton
          list={list}
          columns={requirementColumns}
          defaultSelectedColumns={[t('requirement.number'), t('product.code'), t('requirement.requireCount')]}
          dataMapper={requirementDataMapper}
          fileName="需求清单.xlsx"
        />
        <ExportExcelButton
          isAll
          loadData={handleExportAll}
          columns={requirementColumns}
          defaultSelectedColumns={[t('requirement.number'), t('product.code'), t('requirement.requireCount')]}
          dataMapper={requirementDataMapper}
          fileName={`${t('requirement.requirementListAll')}.xlsx`}
        />
      </Flex>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={listLoading}
        >
          {({ submit }) => (
            <>
              {pageId !== PageId.PUR_OM && (
                <RangePicker
                  value={[startTime, endTime]}
                  onChange={value =>
                    updateDraftFilters({
                      startTime: value?.[0] ?? null,
                      endTime: value?.[1] ?? null,
                    })
                  }
                />
              )}
              <Tooltip title={t("requirement.listSearchTip")}>
                <Input
                  placeholder={t("requirement.listSearchTip")}
                  style={{ width: 200 }}
                  value={keyword}
                  onChange={event =>
                    updateDraftFilters({ keyword: event.target.value })
                  }
                  onPressEnter={submit}
                />
              </Tooltip>
            </>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  );

  const columns: TableProps<Requirement>["columns"] = [
    {
      title: t("sale.innerNumber"),
      key: "innerNumber",
      dataIndex: ["innerNumber"],
      hidden: getLabelShow("innerNumber"),
      width: 200,
      fixed: "left",
      sorter: (a, b) => {
        const valA = a?.saleOrder?.innerNumber ?? "";
        const valB = b?.saleOrder?.innerNumber ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, record) => (
        <SText text={record.saleOrder?.innerNumber ?? ""} />
      ),
    },
    {
      title: t("requirement.number"),
      key: "number",
      dataIndex: "number",
      width: 200,
      sorter: (a, b) => {
        const valA = a.number ?? "";
        const valB = b.number ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, record) => (
        <Typography.Text
          ellipsis={{ tooltip: record.number ?? "" }}
          style={{ color: '#1890ff', cursor: 'pointer' }}
          onClick={() => setDetail(record)}
        >
          {record.number ?? ""}
        </Typography.Text>
      ),
    },
    {
      title: <SText text={t("requirement.orderDate")} />,
      dataIndex: "createdAt",
      width: 120,
      render: (_, item) => (
        <SText
          text={item?.createdAt ? dateFormat(dayjs(item?.createdAt)) : ""}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: t("product.productInfo"),
      hidden: getLabelShow("product"),
      children: [
        {
          title: <SText text={t("product.code")} />,
          dataIndex: "code",
          width: 100,
          sorter: (a, b) => {
            const valA = a.product?.code ?? "";
            const valB = b.product?.code ?? "";
            return valA.localeCompare(valB);
          },
          render: (_, item) => (
            <SText text={item?.product?.code ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("product.name")} />,
          dataIndex: "name",
          width: 100,
          render: (_, item) => (
            <SText text={item?.product?.name ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: <SText text={t("product.brand1")} />,
          dataIndex: "model",
          width: 120,
          render: (_, item) => (
            <SText
              text={item?.product?.model ?? ""}
              style={{ width: "100%" }}
            />
          ),
        },
        {
          title: <SText text={t("product.uom")} />,
          dataIndex: "uom",
          width: 80,
          render: (_, item) => (
            <SText text={item?.product?.uom ?? ""} style={{ width: "100%" }} />
          ),
        },
        {
          title: t("product.materialProcess"),
          key: "process",
          width: 120,
          sorter: (a, b) => {
            const valA = a?.product?.process ?? "";
            const valB = b?.product?.process ?? "";
            return valA.localeCompare(valB);
          },
          render: (_, record) => <SText text={record?.product?.process ?? ''} />,
        },
      ],
    },
    {
      title: t("requirement.requireCount"),
      key: "requireCount",
      width: 120,
      hidden: getLabelShow("requireCount"),
      render: (_, record) => <SText text={numFormmater(Number(record?.requireCount || 0))} />,
    },
    {
      title: t("requirement.mrd"),
      key: "mrd",
      width: 100,
      hidden: getLabelShow("mrd"),
      render: (_, record) => <SText text={record?.mrd ? dateFormat(dayjs(record?.mrd)) : ""} />
    },
    {
      title: t("requirement.eps"),
      key: "eps",
      width: 100,
      hidden: getLabelShow("eps"),
      render: (_, record) =>
        <SText text={record?.eps ? dateFormat(dayjs(record?.eps)) : ""} />,
    },
    {
      title: t("requirement.estimatedCost"),
      key: "estimatedCost",
      hidden: getLabelShow("estimatedCost"),
      width: 120,
      render: (_, record) => <SText text={record?.estimatedCost} />,
    },
    {
      title: t("common.desc"),
      key: "desc",
      width: 120,
      render: (_, record) => <SText text={record.desc} />,
    },
    {
      title: <SText text={t("common.creator")} />,
      dataIndex: "creator",
      width: 100,
      render: (_, item) => {
        let userData = item.creator;
        return (
          userData && <Flex>
            <div>
              <WMSAvatar creator={userData} uniChar={userData.id}
                avatarId={userData.avatar}
                size={24} alt={getUserAvatarTip(userData)}
              />
            </div>
            <SText text={getUserName(userData)} />
          </Flex>
        )
      },
    },
    {
      title: t("requirement.salePrice"),
      key: "saleOrder",
      fixed: "right",
      width: 120,
      dataIndex: ["saleOrder", "totalRealAmount"],
      hidden: getLabelShow("saleItem"),
      render(val, row) {
        return <SText text={val ? numFormmater(Number(val)) : ''} />
      }
    },
    {
      title: t("requirement.mateCost"),
      key: "saleOrder",
      fixed: "right",
      width: 120,
      dataIndex: ['saleOrder', 'bomCost'],
      hidden: getLabelShow("saleItem"),
      render: (value, row) => {
        const rawBomCost = row?.saleOrder?.bomCost;
        const bomCost = Number(rawBomCost);
        // 处理空值和非数字值
        return <SText text={rawBomCost && !Number.isNaN(bomCost) ? numFormmater(bomCost) : t("requirement.notCount")} />
      }
    },
    {
      title: t("requirement.netProfit"),
      key: "saleOrder",
      fixed: "right",
      width: 120,
      dataIndex: ['saleOrder', 'netProfit'],
      hidden: getLabelShow("saleItem"),
      render: (value) => {
        const netProfit = Number(value); // 处理空值和非数字值
        return <SText text={value && !Number.isNaN(netProfit) ? numFormmater(netProfit, 5) : t("requirement.notCount")} />
      }
    },
    {
      title: <SText text={t("requirement.purchaseProgress")} />,
      width: 80,
      hidden: getLabelShow("purchaseProgress"),
      fixed: 'right',
      render: (_, item) => <SText text={`${item.puredItemCount}/${item.itemCount}`} style={{ width: "100%" }} />
      // render: (_, item) => {
      //   if (item.items) return <SText text={`${item.items.filter(s => s.equotationRequest && s.equotationRequest.purchaseItem).length}/${item.itemCount}`} style={{ width: "100%" }} />
      // },
    },
    {
      title: <SText text={t("requirement.quotationProgress")} />,
      width: 120,
      hidden: getLabelShow("quotationProgress"),
      fixed: 'right',
      render: (_, item) => <SText text={`${item.quoteItemCount}/${item.itemCount}`} style={{ width: "100%" }} />
    },
    {
      title: t("requirement.materialProgress"),
      key: "materialProgress",
      width: 120,
      fixed: "right",
      hidden: getLabelShow("materialProgress"),
      render: (_, item) => <SText text={`${item.readyCount}/${item.itemCount}`} style={{ width: "100%" }} />

      // render: (_, record) => {
      //   let isReady = 0;
      //   record.items.forEach(item => {
      //     if (item.inCount && Number(item.inCount) >= Number(item.useAmount)) isReady += 1
      //   })
      //   return <SText text={`${isReady} / ${record.itemCount}`} />
      // },
    },
    {
      title: <SText text={t("sale.reRemark")} />,
      key: "confirm",
      fixed: "right",
      width: 160,
      hidden: pageId !== PageId.MC_PASSED,
      render: (_, record) => (
        <SText text={record.confirms.find(con => con.type === RequirementConfirmTypeEnum.MATERIAL_CONTROL)?.desc ?? ''} />
      ),
    },
    {
      title: <SText text={t("requirement.passStatus")} />,
      key: "passStatus",
      fixed: "right",
      width: 160,
      render: (_, record) => (
        <RequirementReview confirms={record?.confirms ?? []} />
      ),
    },
    {
      title: t("common.opt"),
      key: "detail",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <Flex gap={10} justify="space-between">
          {pageId === PageId.PUR_ALL_PASSED && (
            <Button type="primary" onClick={() => setPurDetail(record)}>
              {t("requirement.goPur")}
            </Button>
          )}
          {pageId !== PageId.PUR_ALL_PASSED && (
            <Tooltip title={t("common.detail")}>
              <Button
                type="primary"
                icon={<GroupOutlined />}
                onClick={() => setDetail(record)}
              />
            </Tooltip>
          )}
          {
            pageId && [PageId.PUR_BE_REJECT, PageId.PUR_PASSED].includes(pageId) && (
              <Button onClick={() => setRequirenment(record)} type="primary">
                {t('requirement.purchaseOrders')}
              </Button>
            )
          }
          {pageId === PageId.PROD_PASSED && (
            <Button
              type="primary"
              onClick={() => setOverIssue(record)}
            >
              {t("reimburse.materialOverIssue")}
            </Button>
          )}
        </Flex>
      ),
    },
  ];

  const fetch = (offset: number, filters: RequirementFilters) => {
    if (params) {
      // 取消上一次请求
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // 创建新的 AbortController
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;

      const maps: {
        [key in string]: RequirementStatusEnum;
      } = {
        [PageId.MC_OFFICIAL_CLOSED]: RequirementStatusEnum.CLOSE,
        [PageId.PROD_OFFICIAL_CLOSED]: RequirementStatusEnum.CLOSE,
        [PageId.PUR_OFFICIAL_CLOSED]: RequirementStatusEnum.CLOSE,
        [PageId.MC_OFFICIAL_DONE]: RequirementStatusEnum.OPEN_DONE,
        [PageId.PROD_OFFICIAL_DONE]: RequirementStatusEnum.OPEN_DONE,
        [PageId.PUR_OFFICIAL_DONE]: RequirementStatusEnum.OPEN_DONE,
        [PageId.MC_OFFICIAL_PENDING]: RequirementStatusEnum.OPEN_PENDING,
        [PageId.PROD_OFFICIAL_PENDING]: RequirementStatusEnum.OPEN_PENDING,
        [PageId.PUR_OFFICIAL_PENDING]: RequirementStatusEnum.OPEN_PENDING,
        [PageId.MC_REQ_MANAGE]: RequirementStatusEnum.OPEN_PENDING,
        [PageId.MC_REQ_M]: RequirementStatusEnum.OPEN_PENDING,
      };

      let status: RequirementStatusEnum =
        (pageId && maps[pageId]) ?? RequirementStatusEnum.OPEN_PENDING;

      // ✅ 直接在 params 对象中传递 controller
      fetchList({
        pagination: { offset, take },
        body: {
          keyword: filters.keyword,
          states: pageId && [PageId.MC_REQ_MANAGE, PageId.MC_REQ_M, PageId.PUR_OM].includes(pageId) ? [] : params.params,
          status,
          mrd: pageId === PageId.PUR_OM ? dayjs().startOf("day").valueOf() : undefined
        },
        startTime: pageId === PageId.PUR_OM
          ? undefined
          : filters.startTime?.startOf("day").valueOf(),
        endTime: pageId === PageId.PUR_OM
          ? undefined
          : filters.endTime?.endOf("day").valueOf(),
        controller, // ✅ 传递 controller 用于取消请求
      }).catch((err) => {
        // 忽略取消的请求
        if (err?.name === 'AbortError') {
          return;
        }
        switch (err) {
          case 10010:
            break;
          default:
            break;
        }
      });
    }
  };

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout
        loading={listLoading}
        dataSource={list}
        columns={columns as ColumnsType<AnyObject>}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        handleChange={offset => fetch(offset, appliedFilters)}
      />

      {detail && pageId && ![PageId.F_BE_REJECT, PageId.F_NOT_PASS, PageId.F_PASSED].includes(pageId) && (
        <RequirementDetail
          isAdmin={pageId && [PageId.MC_REQ_MANAGE, PageId.MC_REQ_M].includes(pageId)}
          number={detail.number}
          id={detail.id}
          open={!!detail}
          onCancel={() => setDetail(null)}
          handleOk={(type?: "close" | "del") => {
            setDetail(null);
            notification.success({ placement: 'top', message: t('order.operationSucc') })
            if (type === "del") {
              rmItem(detail.id);
            } else {
              updateItem(
                { ...detail, status: RequirementStatusEnum.CLOSE },
                "id"
              );
            }
          }}
          handleCancel={() => setDetail(null)}
          handleProgress={requirement => updateItem({ ...detail, quoteItemCount: requirement.quoteItemCount })}
          onUpdateRequirement={requirement => {
            setDetail(requirement);
            updateItem(requirement);
          }}
        />
      )}
      {detail && pageId && [PageId.F_BE_REJECT, PageId.F_NOT_PASS, PageId.F_PASSED].includes(pageId) && (
        <FinanceDetail
          number={detail.number}
          id={detail.id}
          open={!!detail}
          handleOk={(type?: "close" | "del") => {
            setDetail(null);
            notification.success({ placement: 'top', message: t('order.operationSucc') })
            if (type === "del") {
              rmItem(detail.id);
            } else {
              updateItem(
                { ...detail, status: RequirementStatusEnum.CLOSE },
                "id"
              );
            }
          }}
          handleCancel={() => setDetail(null)}
          updateCb={(requirement) => updateItem({ ...requirement })}
          destroyOnClose
        />
      )}

      {purDetail && (
        <PurchaseModal reqNumber={purDetail.number}
          open={!!purDetail}
          onOk={(data) => {
            if (data.length > 0) {
              // let newDetail = _.cloneDeep(purDetail)
              // newDetail.items = newDetail.items.map(i => {
              //   if (i.quotationRequest) {
              //     let newQuotationRequest = data.find(d => i.quotationRequest && d.id === i.quotationRequest.id)
              //     if (newQuotationRequest) i.quotationRequest = newQuotationRequest
              //   }
              //   return i
              // })
              // updateItem({ ...newDetail })
            }
            setPurDetail(null)
          }}
          onCancel={() => setPurDetail(null)}
          id={purDetail.id}
        />
      )}

      {pageId && [PageId.PUR_BE_REJECT, PageId.PUR_PASSED].includes(pageId) && requirenment && (
        <RequirementPurchaseOrders requirenment={requirenment} closeModal={() => setRequirenment(null)} />
      )}

      {overIssue && (
        <AddMaterialOverIssue
          open={!!overIssue}
          requirementId={overIssue.id}
          requirementNumber={overIssue.number}
          onClose={() => setOverIssue(null)}
          onSuccess={() => setOverIssue(null)}
        />
      )}

      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
    </Flex>
  );
};

export default RequirementList;
